import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
 Component,
 ElementRef,
 Inject,
 Input,
 PLATFORM_ID,
 ViewChild,
 WritableSignal,
 afterNextRender,
 computed,
 effect,
 inject,
 signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RoomInfo } from '@core/models/domains/room';
import { RialToTomanPipe } from '@shared/pipes/rial-to-toman.pipe';
import { HotelInfoUiService } from '../../services/hotel-info-ui.service';
import { DatepickerService } from '@shared/services/datepicker.service';
import { MomentService } from '@core/services/moment.service';
import { WebengageService } from '@core/services/webengage.service';
import { WINDOW } from '../../accommodation-info.module';
import { PdpStickyPriceDetail } from '@core/models/domains/general.model';
import { SearchBoxService } from '@shared/services/serach-box.service';

@Component({
 selector: 'iho-pdp-mobile-affix',
 standalone: true,
 imports: [CommonModule, RialToTomanPipe, MatButtonModule, RouterModule],
 templateUrl: './pdp-mobile-affix.component.html',
 styleUrls: ['./pdp-mobile-affix.component.scss'],
})
export class PdpMobileAffixComponent {
 @Input() roomFullName = '';
 @Input() accommodation = '';
 @ViewChild('accordion') accordion!: ElementRef;
 @ViewChild('accordionBody') accordionBody!: ElementRef;
 router = inject(Router);
 route = inject(ActivatedRoute);

 pdpUiService = inject(HotelInfoUiService);
 datePickerService = inject(DatepickerService);
 momentService = inject(MomentService);
 webengageService = inject(WebengageService);
 searchBoxService = inject(SearchBoxService);

 showAffix = computed(() => this.datePickerService._state());

 selectedComboRooms = computed(() => this.pdpUiService.selectedComboRooms());
 selectedComboPrice = 0;

 selectedRooms: Array<RoomInfo & { count: number; id: number; name: string }> = [];
 allRoomsCount = 0;
 lowestSelectedRoomsPrice = 0;
 lowestPriceInAllRooms = 0;
 totalPrice = 0;
 nights = 0;
 startDate = '';
 endDate = '';
 hotelId = -1;
 selectedRoomsJson = '';
 navigatorVal: any = isPlatformBrowser(this.platformId)
  ? /Android|iPhone/.test(this.window.navigator.userAgent)
  : undefined;

 constructor(
  @Inject(PLATFORM_ID) private platformId: Object,
  @Inject(WINDOW) private window: Window
 ) {
  effect(() => {
   this.selectedRooms = this.pdpUiService.selectedRooms();
   this.nights = this.pdpUiService.nights;
   // this.nights = this.pdpUiService.hotelInfo().SearchSection.Night
   // this.nights = this.pdpUiService.hotelInfo().HotelHeader.Price.Nights
   const [startDate, endDate] = this.pdpUiService.searchBoxService.getStartAndEndDate();
   this.startDate = startDate;
   this.endDate = endDate;
   this.hotelId = this.pdpUiService.hotelId;

   if (this.selectedComboRooms()?.length)
    this.selectedComboPrice = this.selectedComboRooms()[0]?.totalPrice?.ihoPrice;

   if (this.selectedRooms?.length) {
    if (isPlatformBrowser(this.platformId) && this.navigatorVal) {
     const reserveCard = document.querySelector('.mobile-last-card') as HTMLElement;
     if (reserveCard) reserveCard.style.display = 'none';
     const rayachatNode = document.getElementById('raychat_widget') as HTMLElement;
     if (rayachatNode) rayachatNode.style.transition = '0.3s ease-in-out all';
     if (rayachatNode) rayachatNode.style.bottom = '210px';
    }
    this.lowestSelectedRoomsPrice = this.calculateLowestPrice();
    this.calculateTotalPrice();
    this.allRoomsCount = this.selectedRooms.reduce(
     (totalCount, eachRoom) => totalCount + eachRoom.count,
     0
    );
   } else {
    if (isPlatformBrowser(this.platformId) && this.navigatorVal) {
     const rayachatNode = document.getElementById('raychat_widget') as HTMLElement;
     if (rayachatNode) rayachatNode.style.transition = '0.3s ease-in-out all';
     if (rayachatNode) rayachatNode.style.bottom = '0px';
    }
    this.totalPrice = 0;
    this.lowestPriceInAllRooms = this.calculateLowestPrice();
    this.allRoomsCount = 0;
   }
   this.prepareDataForSendingToFollowReservation();
  });
 }

 calculateLowestPrice(): number {
  if (this.selectedRooms?.length) {
   return Math.min(
    ...this.selectedRooms!.map((room) => (room.roomPrice ? room.roomPrice.price.ihoPrice : 0))
   );
  } else return this.findLowestPriceInAllRooms();
 }

 findLowestPriceInAllRooms(): number {
  return this.pdpUiService.allRooms().reduce((min, roomInfo) => {
   const currentMin = Math.min(
    ...roomInfo.map((room) => (room.roomPrice ? room.roomPrice.price.ihoPrice : 0))
   );
   return Math.min(min, currentMin);
  }, Infinity);
 }

 calculateTotalPrice() {
  // use the reduce function to sum up the ihoPrice values of all rooms
  this.totalPrice = this.selectedRooms.reduce(
   (total, room) => total + room.roomPrice.price.ihoPrice * room.count,
   0
  );
 }

 removeComboRoomFromSelection() {
  this.pdpUiService.removeComboRoomsSelection();
 }

 toggleSelectedRoomsAccordion() {
  if (this.accordion) this.accordion.nativeElement.classList.toggle('is-open');
 }

 removeSpecificRoom(roomId: number) {
  this.pdpUiService.resetCountSpecific.set(roomId);
  this.pdpUiService.selectedRooms.update((selectedRooms) =>
   selectedRooms.filter((room) => {
    return room.id != roomId;
   })
  );
  this.selectedRooms = this.pdpUiService.selectedRooms();
 }

 /**
  * @description کار این متد اینه که از اتاق های انتخاب شده، یه دیتای مپ شده میسازه و
  * همون دیتا رو باید به صورت پارامس ارسال بکنیم برای صفحه پیگیری رزرو
  */
 prepareDataForSendingToFollowReservation() {
  const selectedRooms: any[] = [];
  this.selectedRooms.forEach((item) => {
   const data = {
    id: item.id,
    count: item.count,
    nonRefundable: item.roomDetail.nonRefundable,
   };
   selectedRooms.push(data);
  });
  this.selectedRoomsJson = JSON.stringify(selectedRooms);
 }

 goToFollowReservation() {
  this.webengageService.track('Room Select', {
   CheckInDate: this.momentService.toGergorian(this.startDate),
   CheckOutDate: this.momentService.toGergorian(this.endDate),
   Destination: this.pdpUiService?.hotelInfo()?.HotelHeader?.Address?.CityName,
   HotelId: this.hotelId,
   Price: this.totalPrice,
   hotelName: this.pdpUiService?.hotelInfo()?.HotelHeader?.FullName,
   RoomName: this.selectedRooms?.map((room) => room.name),
   RoomNumber: this.selectedRooms?.length,
   numberOfNights: this.pdpUiService?.nights,
   hotelType: this.pdpUiService?.hotelInfo()?.HotelHeader?.TypeName,
   hotelRate: +this.pdpUiService?.hotelInfo()?.HotelHeader?.Score?.ScoreNumber,
   hotelGrade: this.pdpUiService?.hotelInfo()?.HotelHeader?.Star?.GradeId || 0,
   roomCapacity: this.selectedRooms?.map((room) => room.reservable),
  });

  let link =
   '/persian/pursuit' +
   ('?hotelId=' + this.hotelId) +
   ('&dateLength=' + this.nights) +
   ('&departing=' + this.startDate) +
   ('&returning=' + this.endDate) +
   ('&selectedRooms=' + this.selectedRoomsJson);

  const _payload = {
   hotelId: this.pdpUiService.hotelId,
   arrivalDate: this.startDate,
   checkoutDate: this.endDate,
   rooms: this.pdpUiService.selectedRooms().map((room) => ({
    id: room.id,
    quantity: room.count,
    nonRefundable: room.roomDetail.nonRefundable,
    index: 0,
   })),
   IsWaitingList: false,
   organizationId: null,
   comboRooms: this.pdpUiService.selectedComboRooms().length
    ? this.pdpUiService.selectedComboRooms()[0].combenationId
    : null,
   AccommodationType: this.searchBoxService.accommodation() == 'ecolodge' ? 2 : 1,
  };

  this.pdpUiService.getReserveUUID(_payload).subscribe((uuid) => {
   if (uuid) {
    window.location.href = window.location.origin + '/persian/pursuit/' + uuid;
   }
  });

//   if (this.pdpUiService.selectedComboRooms().length)
//    link = link + '&comboRooms=' + this.pdpUiService.selectedComboRooms()[0].combenationId;

//   location.href = location.origin + link;
 }
}
